import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as Yup from "yup";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import {
  Clock3,
  ExternalLink,
  Megaphone,
  MessageSquareText,
  Pin,
  PinOff,
  Plus,
  Trash2,
} from "lucide-react";
import { GetAnnouncements } from "../../api/Announcement/GetAllAnnouncements";
import Selectbox, { option } from "../../components/Shared/SelectBox";
import { GetPinnedAnnouncement } from "../../api/Announcement/GetPinnedAnnoucement";
import Input from "../../components/Shared/Input";
import Button from "../../components/Shared/Button";
import { AddNewAnnouncement } from "../../api/Announcement/AddAnnouncement";
import { queryClient } from "../../main";
import { EditAnnouncement } from "../../api/Announcement/EditAnnouncement";
import DeleteModal from "../../components/Shared/DeleteModal";
import { DeleteAnnouncement } from "../../api/Announcement/DeleteAnnouncement";
import Spinner from "../../components/Shared/Spinner";
import { ButtonTypes } from "../../utils/consts";

const validationSchema = Yup.object({
  title: Yup.string().required("Title is required"),
  message: Yup.string().required("Message is required"),
});

const Announcement = () => {
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<option | null>(null);
  const [countdown, setCountdown] = useState<{
    [key: string]: { days: number; hours: number; minutes: number; seconds: number };
  }>({});
  const [isOpen, setIsOpen] = useState(false);

  const calculateEndDate = (
    createdAt: string | number | Date,
    deadline: string
  ) => {
    if (!deadline) return "";

    const [hours, minutes, seconds] = deadline.split(":").map(Number);

    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      isNaN(seconds) ||
      hours < 0 ||
      minutes < 0 ||
      seconds < 0
    ) {
      console.error("Invalid deadline format:", deadline);
      return "";
    }

    const createdTime = new Date(createdAt).getTime();
    const deadlineInMilliseconds =
      hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;

    return new Date(createdTime + deadlineInMilliseconds).toISOString();
  };

  const { mutate } = useMutation({
    mutationFn: AddNewAnnouncement,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      toast.success("Announcement Added");
      formik.setSubmitting(false);
      formik.resetForm();
    },
    onError: () => {
      formik.setSubmitting(false);
    },
  });

  const { mutate: mutatePinned } = useMutation({
    mutationFn: ({ isPin, id }: { isPin: boolean; id: string }) =>
      EditAnnouncement({ isPin }, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["announcements"] });
      queryClient.invalidateQueries({ queryKey: ["pinnedAnnouncements"] });
      toast.success("Announcement Updated Successfully");
    },
    onError: (error) => {
      toast.error("Failed to update the announcement");
      console.error(error);
    },
  });

  const { data: AnnouncementsResponse, isLoading: isAnnouncementLoading } =
    useQuery({
      queryFn: GetAnnouncements,
      queryKey: ["announcements"],
      staleTime: 0,
      refetchInterval: 0,
    });

  const {
    data: PinnedAnnouncementsResponse,
    isLoading: isPinnedAnnouncementLoading,
  } = useQuery({
    queryFn: GetPinnedAnnouncement,
    queryKey: ["pinnedAnnouncements"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const announcements = AnnouncementsResponse?.data?.AnnouncementData || [];
  const announcementOptions = announcements.map((announcement) => ({
    id: announcement.id,
    name: announcement.title,
    code: announcement.message,
  }));

  const pinnedAnnouncements =
    PinnedAnnouncementsResponse?.data?.AnnouncementData || [];
  const pinnedAnnouncementOptions = pinnedAnnouncements.map((announcement) => {
    const endDateISO = calculateEndDate(
      announcement.createdAt,
      announcement.deadline
    );
    return {
      id: announcement.id,
      name: announcement.title,
      code: announcement.message,
      endDate: endDateISO,
      forward_link: announcement.forward_link,
      forward_text: announcement.forward_text,
    };
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      message: "",
      deadline: "",
      forward_text: "",
      forward_link: "",
    },
    validationSchema,
    onSubmit: (values) => {
      mutate(values);
    },
  });

  const handlePinAnnouncement = () => {
    if (selectedAnnouncement) {
      mutatePinned({ isPin: true, id: selectedAnnouncement.id! });
    }
  };

  const handleUnpinAnnouncement = (id: string) => {
    mutatePinned({ isPin: false, id });
  };

  const handleDelete = async (id: string) => {
    await DeleteAnnouncement(id);
    setSelectedAnnouncement(null);
    toast.success("Announcement deleted successfully");
  };

  const calculateTimeLeft = (createdAt: string, deadline: string) => {
    const createdTime = new Date(createdAt).getTime();
    const [hours, minutes, seconds] = deadline.split(":").map(Number);
    const deadlineInMilliseconds =
      hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000;
    const actualDeadlineTime = createdTime + deadlineInMilliseconds;
    const difference = actualDeadlineTime - new Date().getTime();

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor(
        (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
      ),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
    };
  };

  useEffect(() => {
    const intervals: { [key: string]: NodeJS.Timeout } = {};

    pinnedAnnouncements.forEach((announcement) => {
      if (announcement.deadline && announcement.createdAt) {
        intervals[announcement.id] = setInterval(() => {
          setCountdown((prev) => ({
            ...prev,
            [announcement.id]: calculateTimeLeft(
              announcement.createdAt,
              announcement.deadline
            ),
          }));
        }, 1000);
      }
    });

    return () => {
      Object.values(intervals).forEach(clearInterval);
    };
  }, [pinnedAnnouncements]);

  if (isAnnouncementLoading || isPinnedAnnouncementLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6 py-4 sm:py-7">
      <DeleteModal
        queryKey="announcements"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        mutationFn={handleDelete}
        itemId={selectedAnnouncement?.id || ""}
        itemName={selectedAnnouncement?.name}
      />

      <section className="min-w-0 overflow-hidden rounded-3xl border border-black/10 bg-white shadow">
        <div className="flex flex-col gap-5 border-b border-black/10 bg-primary px-4 py-5 text-white sm:px-6 sm:py-6 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase text-white/70">
              Broadcast center
            </p>
            <h1 className="mt-1 break-words text-2xl font-bold sm:text-3xl">
              Announcements
            </h1>
          </div>
          <div className="grid w-full grid-cols-2 gap-3 text-center sm:w-auto sm:min-w-[320px]">
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{announcements.length}</p>
              <p className="text-xs font-semibold text-white/70">Total</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">
                {pinnedAnnouncementOptions.length}
              </p>
              <p className="text-xs font-semibold text-white/70">Pinned</p>
            </div>
          </div>
        </div>

        <div className="grid min-w-0 gap-6 p-4 sm:p-5 xl:grid-cols-[minmax(0,1fr)_420px] xl:p-6">
          <section className="min-w-0 space-y-6">
            <div className="min-w-0 rounded-2xl border border-black/10 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-2xl bg-primary text-white">
                  <Megaphone size={20} />
                </span>
                <div>
                  <h2 className="text-xl font-bold text-textPrimary">
                    Current Announcement
                  </h2>
                  <p className="text-sm font-medium text-lightGray">
                    Visible pinned announcements
                  </p>
                </div>
              </div>

              <div className="grid min-w-0 gap-4 lg:grid-cols-2">
                {pinnedAnnouncementOptions.length ? (
                  pinnedAnnouncementOptions.map((announcement) => (
                    <article
                      className="min-w-0 overflow-hidden rounded-2xl border border-black/10 bg-dashboardBg p-4 sm:p-5"
                      key={announcement.id}
                    >
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="truncate text-lg font-bold text-textPrimary">
                            {announcement.name}
                          </h3>
                          <p className="mt-1 whitespace-pre-wrap break-words text-sm font-medium text-lightGray [overflow-wrap:anywhere]">
                            {announcement.code}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleUnpinAnnouncement(announcement.id)}
                          className="grid size-10 shrink-0 place-items-center rounded-2xl bg-white text-primary shadow-sm transition hover:bg-secondary hover:text-white"
                          aria-label="Unpin announcement"
                        >
                          <PinOff size={18} />
                        </button>
                      </div>

                      <div className="grid min-w-0 gap-3 text-sm font-semibold text-lightGray">
                        {announcement.endDate && (
                          <>
                            <p className="flex min-w-0 items-center gap-2 rounded-xl bg-white px-3 py-2">
                              <Clock3 className="shrink-0" size={16} />
                              <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                                {new Date(announcement.endDate).toLocaleString()}
                              </span>
                            </p>
                            <p className="min-w-0 break-words rounded-xl bg-secondary/10 px-3 py-2 text-primary [overflow-wrap:anywhere]">
                              Time left: {countdown[announcement.id]?.days ?? 0}d{" "}
                              {countdown[announcement.id]?.hours ?? 0}h{" "}
                              {countdown[announcement.id]?.minutes ?? 0}m{" "}
                              {countdown[announcement.id]?.seconds ?? 0}s
                            </p>
                          </>
                        )}
                        {announcement.forward_text && (
                          <p className="flex min-w-0 items-start gap-2 rounded-xl bg-white px-3 py-2">
                            <MessageSquareText className="mt-0.5 shrink-0" size={16} />
                            <span className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                              {announcement.forward_text}
                            </span>
                          </p>
                        )}
                        {announcement.forward_link && (
                          <p className="flex min-w-0 items-center gap-2 rounded-xl bg-white px-3 py-2">
                            <ExternalLink className="shrink-0" size={16} />
                            <span className="min-w-0 truncate">
                              {announcement.forward_link}
                            </span>
                          </p>
                        )}
                      </div>
                    </article>
                  ))
                ) : (
                  <div className="col-span-full rounded-2xl border border-dashed border-black/15 bg-dashboardBg p-5 text-center sm:p-8">
                    <p className="text-lg font-bold text-textPrimary">
                      No pinned announcements
                    </p>
                    <p className="mt-1 text-sm font-medium text-lightGray">
                      Choose one from the control panel.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="min-w-0 rounded-2xl border border-black/10 bg-white p-4 shadow-sm sm:p-5">
              <div className="mb-5 flex items-center gap-3">
                <span className="grid size-11 place-items-center rounded-2xl bg-secondary/10 text-secondary">
                  <Plus size={20} />
                </span>
                <div>
                  <h2 className="text-xl font-bold text-textPrimary">
                    Add New Announcement
                  </h2>
                  <p className="text-sm font-medium text-lightGray">
                    Create a new dashboard/site broadcast
                  </p>
                </div>
              </div>

              <form onSubmit={formik.handleSubmit} className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    value={formik.values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.title && formik.errors.title
                        ? formik.errors.title
                        : ""
                    }
                    name="title"
                    title="Title"
                    inputStyles="!h-12"
                  />
                  <Input
                    value={formik.values.deadline}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="deadline"
                    title="Deadline"
                    placeholder="00:00:00 (Optional)"
                    inputStyles="!h-12"
                  />
                </div>
                <Input
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.message && formik.errors.message
                      ? formik.errors.message
                      : ""
                  }
                  name="message"
                  title="Message"
                  inputStyles="!h-12"
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    value={formik.values.forward_link}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="forward_link"
                    title="Forward Link"
                    inputStyles="!h-12"
                  />
                  <Input
                    value={formik.values.forward_text}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    name="forward_text"
                    title="Forward Text"
                    inputStyles="!h-12"
                  />
                </div>

                <Button
                  disabled={formik.isSubmitting}
                  className="ml-auto !h-12 w-full !rounded-2xl !text-sm font-bold sm:w-56"
                  variant="primary"
                  type={ButtonTypes.SUBMIT}
                >
                  <Plus size={18} />
                  Add Announcement
                </Button>
              </form>
            </div>
          </section>

          <aside className="min-w-0 rounded-2xl border border-black/10 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-5 flex items-center gap-3">
              <span className="grid size-11 place-items-center rounded-2xl bg-primary/10 text-primary">
                <Pin size={20} />
              </span>
              <div>
                <h2 className="text-xl font-bold text-textPrimary">
                  Pin Control
                </h2>
                <p className="text-sm font-medium text-lightGray">
                  Change the active announcement
                </p>
              </div>
            </div>

            <Selectbox
              title="Select Announcement"
              selectOptions={announcementOptions}
              placeholder="Choose an announcement"
              selected={selectedAnnouncement}
              setSelected={setSelectedAnnouncement}
            />

            {selectedAnnouncement ? (
              <div className="mt-5 min-w-0 rounded-2xl border border-secondary/20 bg-secondary/10 p-4">
                <h3 className="break-words text-lg font-bold text-textPrimary [overflow-wrap:anywhere]">
                  {selectedAnnouncement.name}
                </h3>
                <p className="mt-2 whitespace-pre-wrap break-words text-sm font-medium text-lightGray [overflow-wrap:anywhere]">
                  {selectedAnnouncement.code}
                </p>
                <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto] xl:grid-cols-1">
                  <Button
                    onClick={handlePinAnnouncement}
                    className="!h-12 w-full !rounded-2xl !text-sm font-bold"
                    variant="primary"
                    type={ButtonTypes.BUTTON}
                  >
                    <Pin size={17} />
                    Pin Announcement
                  </Button>
                  <button
                    type="button"
                    onClick={() => setIsOpen(true)}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-2xl border border-red-100 bg-white px-4 text-sm font-bold text-red-600 transition hover:bg-red-50"
                  >
                    <Trash2 size={17} />
                    Delete
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border border-dashed border-black/15 bg-dashboardBg p-6 text-center text-sm font-semibold text-lightGray">
                Select an announcement to pin, preview, or delete.
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default Announcement;
